import { OrderEvent } from 'store/orders/orders.types'
import {
  CreateTripPayload,
  ListTripsResponse,
  TripPayload,
  TripResponse,
  TripTask,
} from './trips.types'

export const transformListTrips = (response: ListTripsResponse) => {
  response.trips.forEach((trip) => {
    trip.tripTasks.sort((a, b) => a.sequenceId - b.sequenceId)
  })

  return response
}

export const DROP_EQUIPMENT_SEQUENCE_BOUND = 100

export const getMaxSequenceId = (data: TripPayload[]) => {
  return data.reduce((max, item) => {
    return item.sequenceId > max ? item.sequenceId : max
  }, 0)
}

export const getMaxMiddleSequenceId = (data: TripPayload[]) =>
  data
    .filter((task) => task.sequenceId < DROP_EQUIPMENT_SEQUENCE_BOUND)
    .reduce((max, task) => Math.max(max, task.sequenceId), -Infinity)

export const getMaxOrderEventId = (data: TripPayload[] | OrderEvent[]) => {
  return (data as (TripPayload | OrderEvent)[]).reduce(
    (max: number, item: TripPayload | OrderEvent) =>
      Math.max(max, item.orderEventId ?? 0),
    0
  )
}

export const getEquipmentHookIds = (
  tripTasks: TripPayload[]
): { tractorId: string | null; trailerId: string | null } => {
  const hookTractor = tripTasks.find(
    (task) => task.type === 'HOOK_TRACTOR' && task.orderEventId == null
  )
  const hookTrailer = [...tripTasks]
    .filter(
      (task) =>
        task.type === 'HOOK_TRAILER' &&
        task.orderEventId == null &&
        task.sourceOrderPkId == null &&
        !task.orderId
    )
    .sort((a, b) => b.sequenceId - a.sequenceId)[0]
  // Entered hook address can remove the equipment hook; fall back to equipment drop.
  const dropTrailer = [...tripTasks]
    .filter(
      (task) =>
        task.type === 'DROP_TRAILER' &&
        task.orderEventId == null &&
        task.sourceOrderPkId == null &&
        !task.orderId
    )
    .sort((a, b) => b.sequenceId - a.sequenceId)[0]

  return {
    tractorId:
      hookTrailer?.tractorId ??
      dropTrailer?.tractorId ??
      hookTractor?.tractorId ??
      null,
    trailerId: hookTrailer?.trailerId ?? dropTrailer?.trailerId ?? null,
  }
}

export const isEquipmentTrailerTask = (task: TripPayload): boolean =>
  task.orderEventId == null &&
  task.sourceOrderPkId == null &&
  !task.orderId &&
  (task.type === 'HOOK_TRAILER' || task.type === 'DROP_TRAILER')

const isAddressLinkedTrailerTask = (
  task: TripPayload,
  type: 'HOOK_TRAILER' | 'DROP_TRAILER'
): boolean =>
  task.type === type &&
  (task.orderEventId != null || task.sourceOrderPkId != null || !!task.orderId)

/**
 * If a trailer section already has an entered/order hook or drop address,
 * drop the matching equipment-only row so the two are not shown together.
 */
export const removeRedundantEquipmentTrailerTasks = (
  tripTasks: TripPayload[]
): TripPayload[] => {
  const trailerIdsWithAddressHook = new Set(
    tripTasks
      .filter(
        (task) =>
          isAddressLinkedTrailerTask(task, 'HOOK_TRAILER') && task.trailerId
      )
      .map((task) => task.trailerId as string)
  )
  const trailerIdsWithAddressDrop = new Set(
    tripTasks
      .filter(
        (task) =>
          isAddressLinkedTrailerTask(task, 'DROP_TRAILER') && task.trailerId
      )
      .map((task) => task.trailerId as string)
  )

  return tripTasks.filter((task) => {
    if (!isEquipmentTrailerTask(task) || task.trailerId == null) return true
    if (
      task.type === 'HOOK_TRAILER' &&
      trailerIdsWithAddressHook.has(task.trailerId)
    ) {
      return false
    }
    if (
      task.type === 'DROP_TRAILER' &&
      trailerIdsWithAddressDrop.has(task.trailerId)
    ) {
      return false
    }
    return true
  })
}

/** Remove equipment hook/drop trailer rows only; keep orders and tractor. */
export const removeEquipmentTrailerTasks = (
  tripTasks: TripPayload[],
  trailerId?: string
): TripPayload[] => {
  const removedTrailerIds = new Set<string>()

  const filtered = tripTasks.filter((task) => {
    if (!isEquipmentTrailerTask(task)) return true
    if (trailerId != null && task.trailerId !== trailerId) return true
    if (task.trailerId) removedTrailerIds.add(task.trailerId)
    return false
  })

  const idsToClear =
    trailerId != null ? new Set([trailerId]) : removedTrailerIds

  if (!idsToClear.size) return filtered

  return filtered.map((task) =>
    task.trailerId != null && idsToClear.has(task.trailerId)
      ? { ...task, trailerId: null }
      : task
  )
}

export const syncOrderTasksWithEquipmentIds = (
  tripTasks: TripPayload[]
): TripPayload[] => {
  const { tractorId, trailerId } = getEquipmentHookIds(tripTasks)
  if (!tractorId && !trailerId) return tripTasks

  return tripTasks.map((task) => {
    const shouldSync =
      task.type === 'PICKUP' ||
      task.type === 'DELIVERY' ||
      task.type === 'HOOK_TRACTOR' ||
      task.type === 'DROP_TRACTOR' ||
      task.type === 'HOOK_TRAILER' ||
      task.type === 'DROP_TRAILER'

    if (!shouldSync) return task

    return {
      ...task,
      tractorId: task.tractorId ?? tractorId,
      trailerId: task.trailerId ?? trailerId,
    }
  })
}

export const normalizeTripTaskSequence = (
  tripTasks: TripPayload[]
): TripPayload[] => {
  const getOrderEventSortRank = (task: TripPayload): number => {
    if (
      task.type === 'PICKUP' ||
      (task.type === 'HOOK_TRAILER' &&
        (task.orderEventId != null || task.sourceOrderPkId != null))
    ) {
      return 0
    }
    if (task.type === 'DELIVERY') return 1
    if (
      task.type === 'DROP_TRAILER' &&
      (task.orderEventId != null || task.sourceOrderPkId != null)
    ) {
      return 2
    }
    return 3
  }

  const isSameOrder = (a: TripPayload, b: TripPayload) =>
    (a.sourceOrderPkId != null && a.sourceOrderPkId === b.sourceOrderPkId) ||
    (a.orderId != null && a.orderId === b.orderId)

  const sortSegmentTasks = (tasks: TripPayload[]) =>
    [...tasks].sort((a, b) => {
      if (isSameOrder(a, b)) {
        const rankDiff = getOrderEventSortRank(a) - getOrderEventSortRank(b)
        if (rankDiff !== 0) return rankDiff
      }
      return (a.sequenceId ?? 0) - (b.sequenceId ?? 0)
    })

  const sortedTasks = [...tripTasks].sort(
    (a, b) => (a.sequenceId ?? 0) - (b.sequenceId ?? 0)
  )
  const hookTractorTasks = sortedTasks.filter(
    (task) => task.type === 'HOOK_TRACTOR' && task.orderEventId == null
  )
  const dropTractorTasks = sortedTasks.filter(
    (task) => task.type === 'DROP_TRACTOR' && task.orderEventId == null
  )
  const trailerHooks = sortedTasks.filter(
    (task) =>
      task.type === 'HOOK_TRAILER' &&
      task.orderEventId == null &&
      task.sourceOrderPkId == null &&
      !task.orderId
  )
  const equipmentDrops = sortedTasks.filter(
    (task) =>
      task.type === 'DROP_TRAILER' &&
      task.orderEventId == null &&
      task.sourceOrderPkId == null &&
      !task.orderId
  )
  const usedTasks = new Set<TripPayload>([
    ...hookTractorTasks,
    ...dropTractorTasks,
    ...trailerHooks,
  ])
  const orderedTasks: TripPayload[] = [...hookTractorTasks]
  const trailerIdsWithHooks = new Set(
    trailerHooks.map((hook) => hook.trailerId).filter(Boolean)
  )

  for (const hook of trailerHooks) {
    const segmentTasks = sortSegmentTasks(
      sortedTasks.filter(
        (task) =>
          !usedTasks.has(task) &&
          task.trailerId === hook.trailerId &&
          !(
            task.type === 'DROP_TRAILER' &&
            task.orderEventId == null &&
            task.sourceOrderPkId == null &&
            !task.orderId
          )
      )
    )
    const trailerDrops = equipmentDrops.filter(
      (task) =>
        !usedTasks.has(task) && task.trailerId === hook.trailerId
    )

    orderedTasks.push(hook, ...segmentTasks, ...trailerDrops)
    segmentTasks.forEach((task) => usedTasks.add(task))
    trailerDrops.forEach((task) => usedTasks.add(task))
  }

  // Equipment drop without equipment hook (entered hook address replaced the hook).
  for (const drop of equipmentDrops) {
    if (usedTasks.has(drop)) continue
    if (drop.trailerId != null && trailerIdsWithHooks.has(drop.trailerId)) {
      continue
    }

    const segmentTasks = sortSegmentTasks(
      sortedTasks.filter(
        (task) =>
          !usedTasks.has(task) &&
          task !== drop &&
          task.trailerId === drop.trailerId
      )
    )

    orderedTasks.push(...segmentTasks, drop)
    segmentTasks.forEach((task) => usedTasks.add(task))
    usedTasks.add(drop)
  }

  orderedTasks.push(
    ...sortSegmentTasks(sortedTasks.filter((task) => !usedTasks.has(task))),
    ...dropTractorTasks
  )

  return orderedTasks.map((task, sequenceId) => ({
    ...task,
    sequenceId: task.type === 'DROP_TRACTOR' ? 100 : sequenceId,
  }))
}

export const prepareTripTasks = (tripTasks: TripPayload[]): TripPayload[] =>
  normalizeTripTaskSequence(
    removeRedundantEquipmentTrailerTasks(
      syncOrderTasksWithEquipmentIds(tripTasks)
    )
  )

export const tripTaskToPayload = (task: TripTask): TripPayload => {
  const orderEvent = task.orderEvent
  const isEnteredDropTrailer =
    task.type === 'DROP_TRAILER' && orderEvent != null && !orderEvent.name

  return {
    type: task.type,
    orderEventId: orderEvent?.orderEventId,
    tractorId: task.tractorId,
    trailerId: task.trailerId,
    name: orderEvent?.name ?? task.name,
    address: orderEvent?.address ?? task.address,
    startTime: orderEvent?.startTime ?? task.startTime,
    estimatedTime: task.estimatedTime ?? orderEvent?.endTime ?? '',
    sequenceId: task.sequenceId,
    orderId: orderEvent?.userOrderId ?? orderEvent?.orderId?.toString(),
    // Enter-address drops are created with an empty name — omit sourceOrderPkId
    // so they keep Remove-only UX after reload.
    sourceOrderPkId: isEnteredDropTrailer ? undefined : orderEvent?.orderId,
    taskStatus: task.status,
    modifiedDate: task.modifiedDate,
    tripTaskId: task.tripTaskId,
    tripId: task.tripId,
  }
}

export const tripResponseToCreatePayload = (
  trip: TripResponse
): CreateTripPayload => ({
  userId: trip.userId,
  userTripId: trip.userTripId,
  firstDriverName: trip.firstDriverName,
  firstDriverId: trip.firstDriverId,
  secondDriverName: trip.secondDriverName,
  secondDriverId: trip.secondDriverId,
  tripType: trip.tripType ?? 'DRIVER_TRIP',
  tripTasks: [...trip.tripTasks]
    .sort((a, b) => a.sequenceId - b.sequenceId)
    .map(tripTaskToPayload),
})
